import { useState, useCallback, useContext } from 'react';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react';
import { ClickAwayListener } from '@mui/material';
import { Edit2 } from 'react-feather';
import { Checkbox } from '@trinity/chat-components/components/presentation/Checkbox.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import cssStyles from './ColumnContent.module.css';

// Order/Ordinal content
interface OrderContentProps {
  order: number | null;
}

export const OrderContent = ({ order }: OrderContentProps) => (
  <span className={cssStyles.cellText}>{order || '-'}</span>
);

// Color content
interface ColorContentProps {
  color: string | null;
  editable?: boolean;
  onChange?: (color: string) => void;
}

export const ColorContent = ({ color, editable = false, onChange }: ColorContentProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const { palette, theme: wl } = useContext(AppThemingContext);

  const { refs, floatingStyles } = useFloating({
    open: isPickerOpen,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const handleColorChange = useCallback((newColor: string) => {
    onChange?.(newColor);
    setIsPickerOpen(false);
  }, [onChange]);

  const togglePicker = useCallback(() => {
    if (editable) {
      setIsPickerOpen(!isPickerOpen);
    }
  }, [editable, isPickerOpen]);

  const handleClickAway = useCallback(() => {
    setIsPickerOpen(false);
  }, []);

  // Sentiment tag colors exact match
  const colors = [
    // First row
    '#E8F7FC', '#F7F7FD', '#FADBE3', '#F2F8E7', '#E0FAFA', '#FCF1EF',
    // Second row
    '#CCECF9', '#DCDCF8', '#F7C0CD', '#E4F1CF', '#C6F5F5', '#F7DAD4',
    // Third row
    '#B0E1F6', '#C1C1F3', '#F4A5B7', '#D6EAB7', '#ACF0F0', '#F2C3B9',
  ];

  // White labeling style for pencil icon
  const pencilIconStyle = wl?.enabled ? {
    color: palette.primary.main,
  } : {
    color: 'var(--pri-02)',
  };

  if (!editable) {
    return (
      <div className={cssStyles.colorContainer}>
        {color && (
          <div
            className={cssStyles.colorSwatch}
            style={{ backgroundColor: color }}
            title={color}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cssStyles.colorContainer}>
      <div
        ref={refs.setReference}
        className={cssStyles.editableColorContainer}
        onClick={togglePicker}
      >
        {color && (
          <div
            className={cssStyles.colorSwatch}
            style={{ backgroundColor: color }}
            title={color}
          />
        )}
        <Edit2
          className={cssStyles.editIcon}
          size={14}
          style={pencilIconStyle}
        />
      </div>

      {isPickerOpen && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={cssStyles.colorPickerPopper}
          >
            <div className={cssStyles.colorPickerGrid}>
              {colors.map((colorOption) => (
                <div
                  key={colorOption}
                  className={cssStyles.colorOption}
                  style={{ backgroundColor: colorOption }}
                  onClick={() => handleColorChange(colorOption)}
                  title={colorOption}
                />
              ))}
            </div>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

// Title content
interface TitleContentProps {
  title: string;
}

export const TitleContent = ({ title }: TitleContentProps) => (
  <span className={cssStyles.cellText}>
    {title}
  </span>
);

// Category content
interface CategoryContentProps {
  category: string | null;
}

export const CategoryContent = ({ category }: CategoryContentProps) => (
  <span className={cssStyles.cellText}>
    {category || 'N/A'}
  </span>
);

// Status content
interface StatusContentProps {
  hidden: boolean;
}

export const StatusContent = ({ hidden }: StatusContentProps) => (
  <span className={cssStyles.cellText}>
    {hidden ? 'Hidden' : 'Published'}
  </span>
);

// Summary toggle content
interface SummaryContentProps {
  includeSummary: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
}

export const SummaryContent = ({ includeSummary, onChange, disabled = false }: SummaryContentProps) => (
  <div className={cssStyles.toggleContainer}>
    <div title={includeSummary ? 'Included in summary' : 'Not included in summary'}>
      <Checkbox
        checked={includeSummary}
        disabled={disabled}
        onChange={e => onChange?.(e.target.checked)} />
    </div>
  </div>
);